/**
 * PLAYER_ROLL (cee-lo) — non-banker shooter rolls 3 dice. Mirror of
 * BankerRollState; only the active-roller field differs
 * (`currentPlayerId` here vs `bankerId` for banker roll).
 *
 * Cee-lo's per-shooter loop transitions PLAYER_BETTING → PLAYER_ROLL
 * → PLAYER_BETTING (next shooter, with shooter announcement). onExit
 * runs pure resolution choreography then a handoff if the next
 * state's active player differs from the one who just rolled.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class PlayerRollState extends State {
  private _enterShooterId: string | null = null;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    this._enterShooterId = update.currentPlayerId ?? null;
    this._resetRollSetup();

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();
    game.instructionBanner?.hide();

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this._updateProfiles(update);
    await this._checkPlayers(update);

    this._showOpponentReadyDice(update.currentPlayerId, 3);

    // Authorization rides a follow-up update (server authorizes post-broadcast)
    // → handled from whichever of onEnter/onUpdate first sees us authorized.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    const game = this.game;
    this._updateProfiles(update);
    await this._checkPlayers(update);

    // No-count re-roll: the server stays in PLAYER_ROLL and re-emits a
    // same-state update carrying the rolled dice (lastRollResult). Play the
    // label (spectators see it here; the local shooter already saw it via the
    // local dice-stop fire — deduped). If it's MY no-count, await the label
    // then re-arm my pickup so I can re-roll in place. A scoring roll
    // transitions away (onExit), so resultNeedsReroll guards it out.
    if (update.lastRollResult) {
      game.handleNewRollResult(update.lastRollResult);
      const localId = game.getLocalPlayerId();
      const mine = update.lastRollResult.playerId === localId;
      if (mine && game.resultNeedsReroll(update.lastRollResult.result)) {
        await game.playerProfilesOverlay?.waitForAllFlyouts();
        this._resetRollSetup();
      }
    }

    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    await this._checkPlayers(update);
    await this.game.playRollResolution(update);

    const next = update.currentPlayerId ?? update.bankerId ?? null;
    const prev = this._enterShooterId;
    if (prev && next && prev !== next) {
      await this.game.runShooterHandoff(update, prev, next);
    }

    // Pre-spawn next round's banker zone before the camera lerps —
    // mirrors the craps PointRollState onExit for parity.
    this.game.primeBetZones();
  }
}
